import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Add, Delete, Edit } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import Container from 'src/components/atoms/Container';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { DeleteDialog } from 'src/components/organisms/DeleteDialog/DeleteDialog';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { useCustomAgGridNavigation } from 'src/hooks/ag-grid/useCustomAgGridNavigation';
import { useChainFurnitureZoneDeleteChanges } from 'src/hooks/chain-furniture-zone/save-changes/useChainFurnitureZoneDeleteChanges';
import {
    ChainFurnitureZoneRow,
    useChainFurnitureZoneRows,
} from 'src/hooks/chain-furniture-zone/table/useChainFurnitureZoneRows';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useDialog } from 'src/hooks/useDialog';
import { AddFurnitureZoneDialog } from './AddFurnitureZoneDialog';
import { defaultColDef, defaultColumns } from './data/columns';

export const ChainFurnitureZone: FC = () => {
    const { isOpen, openDialog, closeDialog } = useDialog();
    const { isOpen: isDeleteOpen, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog();
    const { data: rows, loading } = useChainFurnitureZoneRows();
    const [selectedRow, setSelectedRow] = useState<ChainFurnitureZoneRow | null>(null);
    const [furnitureZoneToDelete, setFurnitureZoneToDelete] = useState<ChainFurnitureZoneRow | null>(null);
    const [deleteFurnitureZone, { loading: deletingFurnitureZone }] = useChainFurnitureZoneDeleteChanges();

    const handleEdit = useCallback(
        (data: ChainFurnitureZoneRow) => {
            setSelectedRow(data);
            openDialog();
        },
        [openDialog]
    );

    const showDeleteDialog = useCallback(
        (data: ChainFurnitureZoneRow) => {
            setFurnitureZoneToDelete(data);
            openDeleteDialog();
        },
        [openDeleteDialog]
    );

    const columns = useMemo(() => {
        return [
            ...defaultColumns,
            {
                headerName: 'Actions',
                field: 'actions',
                cellRenderer: (params: ICellRendererParams<ChainFurnitureZoneRow>) => {
                    return deletingFurnitureZone && furnitureZoneToDelete?.id === params.data?.id ? (
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                Deleting...
                            </Typography>
                            <CircularProgress size={20} />
                        </Stack>
                    ) : (
                        <Stack direction="row" sx={{ justifyContent: 'space-around', alignItems: 'center' }}>
                            <IconButton color="info" onClick={() => params.data && handleEdit(params.data)}>
                                <Edit />
                            </IconButton>
                            <IconButton
                                disabled={deletingFurnitureZone}
                                color="error"
                                onClick={() => params.data && showDeleteDialog(params.data)}
                            >
                                <Delete />
                            </IconButton>
                        </Stack>
                    );
                },
                cellStyle: { textAlign: 'center' },
                sortable: false,
                filter: false,
            },
        ];
    }, [deletingFurnitureZone, furnitureZoneToDelete, handleEdit, showDeleteDialog]);

    const handleDelete = useCallback(() => {
        if (furnitureZoneToDelete) {
            deleteFurnitureZone({ id: furnitureZoneToDelete.id });
            closeDeleteDialog();
        }
    }, [closeDeleteDialog, deleteFurnitureZone, furnitureZoneToDelete]);

    const hideDeleteDialog = useCallback(() => {
        setFurnitureZoneToDelete(null);
        closeDeleteDialog();
    }, [closeDeleteDialog]);

    const handleClose = useCallback(() => {
        setSelectedRow(null);
        closeDialog();
    }, [closeDialog]);

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            openDialog();
        },
    });

    const showSnackbar = useSnackbar();
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    useCustomAgGridNavigation({ api: gridApi, tableWrapperRef });

    const onGridReady = useCallback(({ api }: GridReadyEvent) => {
        setGridApi(api);
    }, []);

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi?.getSelectedRows();
            if (selectedRows?.length === 1) {
                handleEdit(selectedRows[0]);
            } else {
                showSnackbar('Please select a single inventory master data zone to edit.', 'warning');
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'DELETE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApi?.getSelectedRows();
            if (selectedRows?.length === 1) {
                showDeleteDialog(selectedRows[0]);
            } else {
                showSnackbar('Please select a single inventory master data zone to delete.', 'warning');
            }
        },
    });

    return (
        <>
            <Stack spacing={2}>
                <SmartModuleTitleCard />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button size="medium" variant="contained" color="info" startIcon={<Add />} onClick={openDialog}>
                        Add new Furniture Zone
                    </Button>
                </Box>
                <Container>
                    <TableWrapper sx={{ flex: 1 }} className="ag-theme-alpine-custom-compact" ref={tableWrapperRef}>
                        <DataTable
                            isNumberTable={false}
                            loading={loading}
                            columns={columns}
                            defaultColDef={defaultColDef}
                            rows={rows}
                            onGridReady={onGridReady}
                            rowSelection={{
                                mode: 'singleRow',
                                checkboxes: false,
                                enableClickSelection: true,
                            }}
                            suppressCellFocus
                        />
                    </TableWrapper>
                </Container>
            </Stack>
            {isOpen && <AddFurnitureZoneDialog isOpen={isOpen} close={handleClose} furnitureZoneRow={selectedRow} />}
            {isDeleteOpen && (
                <DeleteDialog
                    open={isDeleteOpen}
                    onCancel={hideDeleteDialog}
                    onDelete={handleDelete}
                    title="Delete inventory master data zone?"
                    content={`Are you sure you want to delete inventory master data zone '${furnitureZoneToDelete?.name}'? Deleting this inventory master data zone is permanent and cannot be undone.`}
                />
            )}
        </>
    );
};
